import React, {useCallback, useEffect, useState, useRef} from 'react';
import { Button, Divider, Flex, Group, Paper, Blockquote, Image, Title, Skeleton } from '@mantine/core';
import {DuplicateImageGroup, ImageData} from '../../types/Image';
import {fetchDuplicatesInImages, mergeDuplicates, fetchImageDetails} from "../../api/Image";

interface RemoveDuplicatesModalProps {
    render: boolean;
    onSkip: () => void;
    inImages: ImageData[];
    onIsReady: () => void;
    onDuplicateRemoved: (removed: ImageData) => void;
    onAllDuplicatesMerged: () => void;

}

export const RemoveDuplicateModal: React.FC<RemoveDuplicatesModalProps> = ({render, onSkip, inImages, onIsReady, onDuplicateRemoved, onAllDuplicatesMerged}) => {
    // Keep a local copy of images that we can modify as duplicates are merged
    const [remainingImages, setRemainingImages] = useState<ImageData[]>(inImages);
    const [loading, setLoading] = useState(false);
    const [duplicateGroups, setDuplicateGroups] = useState<DuplicateImageGroup[]>([]);
    // Request counter for debugging
    const requestCount = useRef<number>(0);
    // Flag to track if we've already checked for duplicates
    const hasCheckedForDuplicates = useRef<boolean>(false);
    // Flag to track if we've already skipped due to no duplicates
    const hasSkipped = useRef<boolean>(false);

    const loadDuplicates = useCallback(async () => {
        // If we've already checked and there were no duplicates, don't check again
        if (hasCheckedForDuplicates.current) {
            console.log(`[Skipped Request] Already checked for duplicates`);
            return;
        }

        requestCount.current += 1;
        const currentRequest = requestCount.current;
        console.log(`[Request #${currentRequest}] Loading duplicates for ${inImages.length} images`);
        setLoading(true);

        try {
            const keys: string[] = inImages.map(i => i.key);
            const response = await fetchDuplicatesInImages(keys);
            
            // Mark that we've checked for duplicates
            hasCheckedForDuplicates.current = true;
            
            console.log(`[Response #${currentRequest}] Found ${response.duplicates.length} duplicate groups`);
            
            if (response.duplicates.length > 0) {
                // The new API already returns thumbnails, so we can use them directly
                setDuplicateGroups(response.duplicates);
            } else {
                // Only skip once
                if (!hasSkipped.current) {
                    hasSkipped.current = true;
                    onSkip();
                    console.log(`[Response #${currentRequest}] No duplicates found, skipping`);
                }
            }
        } catch (error) {
            console.error(`[Error #${currentRequest}] Error fetching duplicates:`, error);
        } finally {
            setLoading(false);
            onIsReady();
        }
    }, [inImages, onIsReady, onSkip]);

    useEffect(() => {
        // Only load duplicates if we haven't checked yet
        if (!hasCheckedForDuplicates.current) {
            loadDuplicates();
        }
    }, [loadDuplicates]);

    // Function to merge duplicates using the main image (index 0) as the one to keep
    const handleMergeDuplicates = async (group: DuplicateImageGroup) => {
        if (group.length === 0) return;
        
        // The image to keep is the first one in the array
        const keepImageKey = group[0].key;
        
        // Remove this group from the UI immediately for better UX
        setDuplicateGroups(prevGroups => 
            prevGroups.filter(g => g[0].key !== keepImageKey)
        );

        try {
            const result = await mergeDuplicates(keepImageKey);
            console.log(`Successfully merged duplicates: kept ${result.kept_key}, merged ${result.merged_keys.length} images`);
            
            // Get the keys of images that were merged (removed)
            const mergedImageKeys = new Set(result.merged_keys);
            console.log('Merged image keys from API:', result.merged_keys);
            
            // Update the remaining images list by removing the merged images
            setRemainingImages(prevImages => {
                const filtered = prevImages.filter(image => !mergedImageKeys.has(image.key));
                console.log(`Filtered remaining images from ${prevImages.length} to ${filtered.length}`);
                return filtered;
            });
            
            // Notify parent components about the merged duplicates
            // Find the actual images that were merged using the keys from the API response
            if (onDuplicateRemoved) {
                // Use the merged_keys from the API response to find which images to remove
                result.merged_keys.forEach(key => {
                    // Find the image with this key in the original group
                    const imageToRemove = group.find(img => img.key === key);
                    if (imageToRemove) {
                        console.log('Notifying parent about removed image:', key);
                        onDuplicateRemoved(imageToRemove);
                    }
                });
            }
            
            // Check if there are any remaining duplicate groups
            const remainingGroups = duplicateGroups.length - 1; // We already removed this group from UI
            console.log(`Remaining duplicate groups: ${remainingGroups}`);
            
            // If there are no more duplicate groups, automatically progress to the next page
            if (remainingGroups === 0) {
                console.log('All duplicates processed, automatically progressing to next page');
                if (onAllDuplicatesMerged) {
                    onAllDuplicatesMerged();
                }
            }
        } catch (error) {
            console.error('Error merging duplicates:', error);
            // Put the group back if there was an error
            setDuplicateGroups(currentGroups => [...currentGroups, group]);
            alert('Failed to merge duplicates. Please try again.');
        }
    };

    // Function to handle keeping all images (skip this duplicate group)
    const handleKeepAll = (group: DuplicateImageGroup) => {
        if (group.length === 0) return;
        
        // Simply remove this group from the UI
        setDuplicateGroups(prevGroups => 
            prevGroups.filter(g => g[0].key !== group[0].key)
        );
        
        // Check if there are any remaining duplicate groups
        const remainingGroups = duplicateGroups.length - 1; // We already removed this group from UI
        console.log(`Remaining duplicate groups after skipping: ${remainingGroups}`);
        
        // If there are no more duplicate groups, automatically progress to the next page
        if (remainingGroups === 0) {
            console.log('All duplicates processed (some skipped), automatically progressing to next page');
            if (onAllDuplicatesMerged) {
                onAllDuplicatesMerged();
            }
        }
    };

    if (loading || !render) {
        return <></>;
    }

    return (<div>
        {duplicateGroups.length === 0 ? (
            <div>No duplicates found</div>
        ) : (
            duplicateGroups.map(group => {
                // Skip empty groups
                if (group.length === 0) return null;
                
                // Use the first image's key as the group identifier
                const mainImageKey = group[0].key;
                
                return (
                    <Paper key={mainImageKey} shadow="sm" radius="lg" withBorder p="xl" mt="lg">
                        <Title order={2}>Duplicate Images</Title>
                        <Divider mt="sm" />
                        <Flex mt="lg" wrap="wrap" gap="5px">
                            {group.map((image, idx) => (
                                <div key={image.key} style={{ width: 'calc((100% - 15px) / 3)', aspectRatio: 1 }}>
                                    {image.thumbnail_url ? (
                                        <Image 
                                            src={image.thumbnail_url} 
                                            alt={`Duplicate ${idx === 0 ? '(Main)' : idx}`} 
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <Skeleton width="100%" height="100%" radius="md" />
                                    )}
                                </div>
                            ))}
                        </Flex>
                        <Group justify="flex-end" gap="sm" mt="lg">
                            <Button color="gray" variant="outline" onClick={() => handleKeepAll(group)}>
                                Keep All
                            </Button>
                            <Button color="blue" onClick={() => handleMergeDuplicates(group)}>
                                Merge Duplicates
                            </Button>
                        </Group>
                    </Paper>
                );
            })
        )}
    </div>);
};

export default RemoveDuplicateModal;


//     const mergeDuplicates = async (group: DuplicateImageGroup) => {
//         try {
//             const apiUrl = "https://mg27jllmfg.execute-api.us-west-2.amazonaws.com/production/duplicates";
//             await axios.post(apiUrl,
//                 {
//                     sub: userId,
//                     key: group.key
//                 },
//                 {
//                     headers: {
//                         'Authorization': `Bearer ${auth.IdToken}`,
//                         'Content-Type': 'application/json'
//                     }
//                 }
//             );
//
//             // todo: this is a bit of a hack, i should really be returning something from the post request that tells
//             //  me all the image keys / face keys that have been merged so that I can filter those out of previous modal
//             // Get all image keys from the duplicate group except the first one (which is kept)
//             const mergedKeys = group.duplicates.slice(1).map(duplicate => duplicate.key);
//             onDuplicatesMerged(mergedKeys);
//         } catch (error) {
//             console.error('Error merging duplicates:', error);
//         }
//     };
//
//     const mergeAllDuplicates = async () => {
//         try {
//             const allMergedKeys: string[] = [];
//             for (const group of duplicateGroups) {
//                 // For each group, get all keys except the first one
//                 const mergedKeys = group.duplicates.slice(1).map(duplicate => duplicate.key);
//                 allMergedKeys.push(...mergedKeys);
//
//                 await mergeDuplicates(group);
//             }
//             onDuplicatesMerged(allMergedKeys);
//         } catch (error) {
//             console.error('Error merging all duplicates:', error);
//         }
//     };
//
//     if (duplicateGroups.length === 0) {
//         return (
//             <div>
//                 <Title order={3} mb="md">No Duplicates Found</Title>
//                 <Text size="sm" mb="lg">No duplicate images were found in your selection.</Text>
//             </div>
//         );
//     }
//
//     return (
//         <div>
//             <Flex justify="space-between" align="center" mb="md">
//                 <Title order={3}>Duplicate Images Found</Title>
//                 <Button
//                     color="blue"
//                     variant="light"
//                     onClick={mergeAllDuplicates}
//                 >
//                     Merge All Duplicates
//                 </Button>
//             </Flex>
//
//             <Text size="sm" mb="lg">
//                 The following duplicate images were found. You can merge them to keep the best quality version.
//             </Text>
//
//             {duplicateGroups.map((group, index) => (
//                 <Paper
//                     key={group.key}
//                     shadow="sm"
//                     radius="md"
//                     withBorder
//                     p="md"
//                     mt={index > 0 ? "md" : undefined}
//                     style={{ backgroundColor: '#f0f0f0' }}
//                 >
//                     <Text fw={500} mb="xs">Duplicate Set {index + 1}</Text>
//                     <Divider mb="md" />
//
//                     <Flex
//                         direction="row"
//                         wrap="wrap"
//                         gap="xs"
//                         justify="flex-start"
//                     >
//                         {group.duplicates.map((image, idx) => (
//                             <div
//                                 key={idx}
//                                 style={{
//                                     position: 'relative',
//                                     width: 'calc((100% - 20px) / 3)',
//                                     aspectRatio: '1 / 1'
//                                 }}
//                             >
//                                 <ImageWithSkeleton
//                                     src={image.thumbnail_url}
//                                     alt={`Duplicate ${idx + 1}`}
//                                     onClick={() => {}}
//                                 />
//                             </div>
//                         ))}
//                     </Flex>
//
//                     <Group justify="flex-end" mt="sm">
//                         <Button
//                             variant="light"
//                             color="blue"
//                             size="sm"
//                             onClick={() => mergeDuplicates(group)}
//                         >
//                             Merge Images
//                         </Button>
//                     </Group>
//                 </Paper>
//             ))}
//         </div>
//     );
// };